import { useEffect, useState } from 'react'
import {
  useRecoilState,
  useRecoilValueLoadable,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil'

import { addAlertQuery, AlertType } from 'services/alert'
import { errorMessage } from 'shared/error-handle'
import { ContainersAPI } from '../api'
import {
  cargoByOrderNumberArguments,
  cargoByOrderNumberRequestId,
  containersByWarehouseRequestId,
  containersTypeByWarehouseRequestId,
  containersTypeWarehouseArguments,
  containersWarehouseArguments,
  emptyContainersIdsRequestId,
  emptyContainersTransferArguments,
  emptyContainersTypeTransferRequestId,
  warehouseFilters,
} from './atom'
import {
  getAsyncCargoByOrderNumber,
  getAsyncContainersByWarehouses,
  getAsyncContainerTypes,
  getAsyncEmptyContainerIdsByWarehouse,
  getAsyncEmptyContainers,
  getAsyncEmptyContainerTypes,
  getContainerTypeById,
  getEmptyContainerTypeById,
} from './selector'

export const useContainers = () => {
  const [warehouseArgs, setWarehouseArguments] = useRecoilState(containersWarehouseArguments)
  const setWarehouseFilters = useSetRecoilState(warehouseFilters)
  const setRequestId = useSetRecoilState(containersByWarehouseRequestId)
  const setAlert = useSetRecoilState(addAlertQuery)
  const warehouseLoadable = useRecoilValueLoadable(getAsyncContainersByWarehouses)
  const resetArguments = useResetRecoilState(containersWarehouseArguments)
  const resetFilters = useResetRecoilState(warehouseFilters)

  useEffect(() => {
    switch (warehouseLoadable.state) {
      case 'hasError':
        setAlert([{ message: errorMessage(warehouseLoadable.contents) }])
        break
      case 'hasValue':
      case 'loading':
      default:
    }
  }, [warehouseLoadable.state])

  const refresh = () => setRequestId((curr: number) => curr + 1)

  const reset = () => {
    resetArguments()
    resetFilters()
  }

  return {
    isLoading: warehouseLoadable.state === 'loading',
    setFilters: setWarehouseFilters,
    setArgs: setWarehouseArguments,
    args: warehouseArgs,
    reset,
    containers: warehouseLoadable.valueMaybe(),
    refreshValues: refresh,
  }
}

export const useCargoByOrderNumber = () => {
  const [args, setArgs] = useRecoilState(cargoByOrderNumberArguments)
  const setRequestId = useSetRecoilState(cargoByOrderNumberRequestId)
  const setAlert = useSetRecoilState(addAlertQuery)
  const loadable = useRecoilValueLoadable(getAsyncCargoByOrderNumber)
  const resetArgs = useResetRecoilState(cargoByOrderNumberArguments)

  useEffect(() => {
    switch (loadable.state) {
      case 'hasError':
        setAlert([{ message: errorMessage(loadable.contents) }])
        break
      case 'hasValue':
      case 'loading':
      default:
    }
  }, [loadable.state])

  const refresh = () => setRequestId((curr: number) => curr + 1)

  return {
    isLoading: loadable.state === 'loading',
    setArgs,
    args,
    cargos: loadable.valueMaybe(),
    reset: resetArgs,
    refreshValues: refresh,
  }
}

export const useEmptyContainersStock = () => {
  const [args, setArgs] = useRecoilState(emptyContainersTransferArguments)
  const setRequestId = useSetRecoilState(emptyContainersTypeTransferRequestId)
  const setAlert = useSetRecoilState(addAlertQuery)
  const stockLoadable = useRecoilValueLoadable(getAsyncEmptyContainerTypes)
  const resetArguments = useResetRecoilState(emptyContainersTransferArguments)

  const refresh = () => setRequestId((curr: number) => curr + 1)

  useEffect(() => {
    switch (stockLoadable.state) {
      case 'hasError':
        setAlert([{ message: errorMessage(stockLoadable.contents) }])
        break
      case 'hasValue':
      case 'loading':
      default:
    }
  }, [stockLoadable.state])

  useEffect(() => {
    if (!args) return

    refresh()
  }, [])

  return {
    isLoading: stockLoadable.state === 'loading',
    setArgs,
    resetArgs: resetArguments,
    args,
    stock: stockLoadable.valueMaybe(),
    refreshValues: refresh,
  }
}

export const useEmptyContainers = () => {
  const [args, setArgs] = useRecoilState(emptyContainersTransferArguments)
  const setRequestId = useSetRecoilState(emptyContainersTypeTransferRequestId)
  const setAlert = useSetRecoilState(addAlertQuery)
  const emptyContainersLoadable = useRecoilValueLoadable(getAsyncEmptyContainers)
  const resetArguments = useResetRecoilState(emptyContainersTransferArguments)

  const refresh = () => setRequestId((curr: number) => curr + 1)

  useEffect(() => {
    switch (emptyContainersLoadable.state) {
      case 'hasError':
        setAlert([{ message: errorMessage(emptyContainersLoadable.contents) }])
        break
      case 'hasValue':
      case 'loading':
      default:
    }
  }, [emptyContainersLoadable.state])

  useEffect(() => {
    if (!args) return

    refresh()
  }, [])

  return {
    isLoading: emptyContainersLoadable.state === 'loading',
    setArgs,
    resetArgs: resetArguments,
    args,
    emptyContainers: emptyContainersLoadable.valueMaybe(),
    refreshValues: refresh,
  }
}

export const useEmptyContainerTypeById = (containerTypeId: string) => {
  const setAlert = useSetRecoilState(addAlertQuery)
  const setRequestId = useSetRecoilState(emptyContainersIdsRequestId)
  const containerTypeLoadable = useRecoilValueLoadable(getEmptyContainerTypeById(containerTypeId))
  const emptyContainersLoadable = useRecoilValueLoadable(
    getAsyncEmptyContainerIdsByWarehouse(containerTypeId)
  )

  const refresh = () => setRequestId((curr: number) => curr + 1)

  useEffect(() => {
    switch (containerTypeLoadable.state) {
      case 'hasError':
        setAlert([{ message: errorMessage(containerTypeLoadable.contents) }])
        break
      case 'hasValue':
      case 'loading':
      default:
    }
  }, [containerTypeLoadable.state])

  useEffect(() => {
    switch (emptyContainersLoadable.state) {
      case 'hasError':
        setAlert([{ message: errorMessage(emptyContainersLoadable.contents) }])
        break
      case 'hasValue':
      case 'loading':
      default:
    }
  }, [emptyContainersLoadable.state])

  useEffect(() => {
    if (containerTypeId === '') return

    refresh()
  }, [])

  return {
    isStockLoading: containerTypeLoadable.state === 'loading',
    isEmptyContainersLoading: emptyContainersLoadable.state === 'loading',
    containerType: containerTypeLoadable.valueMaybe(),
    emptyContainers: emptyContainersLoadable.valueMaybe(),
  }
}

export const useWarehouseStock = () => {
  const [args, setArgs] = useRecoilState(containersTypeWarehouseArguments)
  const setRequestId = useSetRecoilState(containersTypeByWarehouseRequestId)
  const setAlert = useSetRecoilState(addAlertQuery)
  const stockLoadable = useRecoilValueLoadable(getAsyncContainerTypes)
  const resetArguments = useResetRecoilState(containersTypeWarehouseArguments)

  const refresh = () => setRequestId((curr: number) => curr + 1)

  useEffect(() => {
    switch (stockLoadable.state) {
      case 'hasError':
        setAlert([{ message: errorMessage(stockLoadable.contents) }])
        break
      case 'hasValue':
      case 'loading':
      default:
    }
  }, [stockLoadable.state])

  useEffect(() => {
    if (!args) return

    refresh()
  }, [])

  return {
    isLoading: stockLoadable.state === 'loading',
    setArgs,
    resetArgs: resetArguments,
    args,
    containerTypes: stockLoadable.valueMaybe(),
    refreshValues: refresh,
  }
}

export const useContainerTypeById = (containerTypeId: string) => {
  const setAlert = useSetRecoilState(addAlertQuery)
  const containerTypeLoadable = useRecoilValueLoadable(getContainerTypeById(containerTypeId))

  useEffect(() => {
    switch (containerTypeLoadable.state) {
      case 'hasError':
        setAlert([{ message: errorMessage(containerTypeLoadable.contents) }])
        break
      case 'hasValue':
      case 'loading':
      default:
    }
  }, [containerTypeLoadable.state])

  return {
    isLoading: containerTypeLoadable.state === 'loading',
    containerType: containerTypeLoadable.valueMaybe(),
  }
}

export const useContainersActions = () => {
  const [processing, setProcessing] = useState<boolean>(false)
  const setAlert = useSetRecoilState(addAlertQuery)

  const transferCargo = async (
    warehouseId: number,
    cargoId: string,
    containerIds: string[],
    comment?: string
  ) => {
    setProcessing(true)

    try {
      await ContainersAPI.transferCargo(warehouseId, cargoId, containerIds, comment)
      setAlert([{ message: 'Груз перемещен', type: AlertType.SUCCESS }])
    } catch (error) {
      const errMsg = errorMessage(error)
      /* eslint-disable-next-line no-console */
      console.error(errMsg)

      setAlert([{ message: 'Ошибка перемещения груза' }])
    } finally {
      setProcessing(false)
    }
  }

  const transferContainers = async (
    warehouseId: number,
    containerIds: string[]
  ): Promise<boolean> => {
    setProcessing(true)

    try {
      await ContainersAPI.transferContainers(warehouseId, containerIds)
      setAlert([{ message: 'Контейнеры перемещены', type: AlertType.SUCCESS }])
      return true
    } catch (error) {
      const errMsg = errorMessage(error)
      /* eslint-disable-next-line no-console */
      console.error(errMsg)

      setAlert([{ message: 'Ошибка перемещения контейнеров' }])
      return false
    } finally {
      setProcessing(false)
    }
  }

  const insepct = async (cargoId: string, containerIds: string[]) => {
    setProcessing(true)

    try {
      await ContainersAPI.inspect(cargoId, containerIds)
      setAlert([{ message: 'Груз проверен', type: AlertType.SUCCESS }])
    } catch (error) {
      const errMsg = errorMessage(error)
      /* eslint-disable-next-line no-console */
      console.error(errMsg)

      setAlert([{ message: 'Ошибка проверки груза (возможен хендшейк)' }])
    } finally {
      setProcessing(false)
    }
  }

  const createCargo = async (
    serialNumber: number,
    warehouseId: number,
    containers: Record<string, string>[]
  ): Promise<boolean> => {
    setProcessing(true)

    try {
      await ContainersAPI.createCargo(serialNumber, warehouseId, containers)
      setAlert([{ message: 'Груз создан', type: AlertType.SUCCESS }])
      return true
    } catch (error) {
      const errMsg = errorMessage(error)
      /* eslint-disable-next-line no-console */
      console.error(errMsg)

      setAlert([{ message: errMsg, duration: 5000 }])
      return false
    } finally {
      setProcessing(false)
    }
  }

  const transferAndInspect = async (
    warehouseId: number,
    cargoId: string,
    containerIds: string[]
  ) => {
    setProcessing(true)

    try {
      await ContainersAPI.transferCargo(warehouseId, cargoId, containerIds)
    } catch (error) {
      const errMsg = errorMessage(error)
      /* eslint-disable-next-line no-console */
      console.error(errMsg)

      setAlert([{ message: 'Ошибка перемещения груза' }])
      setProcessing(false)
      return
    }

    try {
      await ContainersAPI.inspect(cargoId, containerIds)
      setAlert([{ message: 'Груз перемещён и проинвентаризирован', type: AlertType.SUCCESS }])
    } catch (error) {
      const errMsg = errorMessage(error)
      /* eslint-disable-next-line no-console */
      console.error(errMsg)

      setAlert([{ message: 'Перемещено, но ошибка инвентаризации' }])
    } finally {
      setProcessing(false)
    }
  }

  return {
    isLoading: processing,
    transferCargo,
    transferContainers,
    inspectContainers: insepct,
    transferAndInspect,
    createCargo,
  }
}

export const useContainerTypeActions = () => {
  const [processing, setProcessing] = useState<boolean>(false)
  const setAlert = useSetRecoilState(addAlertQuery)

  const add = async (warehouseId: number, containerId: string, amount: number) => {
    setProcessing(true)

    try {
      await ContainersAPI.addContainers(warehouseId, containerId, amount)
      setAlert([{ message: 'Контейнер создан', type: AlertType.SUCCESS }])
    } catch (error) {
      const errMsg = errorMessage(error)
      /* eslint-disable-next-line no-console */
      console.error(errMsg)

      setAlert([{ message: 'Ошибка создания контейнера' }])
    } finally {
      setProcessing(false)
    }
  }

  const remove = async (warehouseId: number, containerId: string, amount: number) => {
    setProcessing(true)

    try {
      await ContainersAPI.removeContainers(warehouseId, containerId, amount)
      setAlert([{ message: 'Контейнер удален', type: AlertType.SUCCESS }])
    } catch (error) {
      const errMsg = errorMessage(error)
      /* eslint-disable-next-line no-console */
      console.error(errMsg)

      setAlert([{ message: 'Ошибка удаления контейнера' }])
    } finally {
      setProcessing(false)
    }
  }

  return {
    isLoading: processing,
    addContainers: add,
    removeContainers: remove,
  }
}
